import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import type { TFetchError } from '@/api/api-error.types';
import { useToast } from '@/shared/design-system';
import { getApiErrorMessage } from '@/utils/api-error-message';

import { GAMMA_ROLES_QUERY_KEYS } from '../gamma-roles/queries';
import { GAMMA_TEAM_QUERY_KEYS } from '../gamma-team/types';
import { gammaRoleRequestsClient } from './client';
import { GAMMA_ROLE_REQUEST_QUERY_KEYS } from './queries';
import type { RejectGammaRoleRequestBody, RoleRequestInput } from './types';

type ApiErrorShape = {
  status?: number;
  response?: { status?: number };
  data?: { statusCode?: number };
};

const isConflict = (error: unknown) => {
  const apiError = error as ApiErrorShape;
  return (apiError.response?.status ?? apiError.data?.statusCode ?? apiError.status) === 409;
};

const useGammaRoleRequestMutation = () => {
  const queryClient = useQueryClient();
  const { add } = useToast();

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [GAMMA_ROLE_REQUEST_QUERY_KEYS.list] });
    queryClient.invalidateQueries({ queryKey: [GAMMA_ROLES_QUERY_KEYS.roles] });
    queryClient.invalidateQueries({ queryKey: [GAMMA_ROLES_QUERY_KEYS.role] });
    queryClient.invalidateQueries({ queryKey: [GAMMA_ROLES_QUERY_KEYS.roleMembers] });
    queryClient.invalidateQueries({ queryKey: [GAMMA_ROLES_QUERY_KEYS.memberPermissions] });
    queryClient.invalidateQueries({ queryKey: [GAMMA_TEAM_QUERY_KEYS.dashboard] });
  }, [queryClient]);
  const showError = useCallback(
    (error: unknown) =>
      add({
        title: getApiErrorMessage(error as TFetchError, 'Could not update this request.'),
        data: { status: isConflict(error) ? 'warning' : 'error' },
      }),
    [add],
  );
  const showSuccess = useCallback(
    (title: string) => add({ title, data: { status: 'success' } }),
    [add],
  );

  return { invalidate, queryClient, showError, showSuccess };
};

const requestResultTitle = (status: string) =>
  status === 'approved' ? 'Change applied.' : 'Request submitted for approval.';

/** Refreshes everything a role request can change, for a caller that batched silent requests. */
export const useRefreshGammaRoleRequestCaches = () => useGammaRoleRequestMutation().invalidate;

type CreateRequestOptions = {
  /**
   * Skips the per-request toasts and cache refresh; a caller batching several requests reports
   * one outcome and refreshes once with `useRefreshGammaRoleRequestCaches`.
   */
  silent?: boolean;
};

export const useCreateGammaRoleRequest = ({ silent = false }: CreateRequestOptions = {}) => {
  const queryClient = useQueryClient();
  const { add } = useToast();

  return useMutation({
    mutationFn: (input: RoleRequestInput) => gammaRoleRequestsClient.create(input),
    onSuccess: (result, { payload }) => {
      if (silent) return;

      queryClient.invalidateQueries({ queryKey: [GAMMA_ROLE_REQUEST_QUERY_KEYS.list] });
      queryClient.invalidateQueries({ queryKey: [GAMMA_ROLES_QUERY_KEYS.roles] });
      queryClient.invalidateQueries({ queryKey: [GAMMA_TEAM_QUERY_KEYS.dashboard] });

      if ('roleId' in payload) {
        queryClient.invalidateQueries({ queryKey: [GAMMA_ROLES_QUERY_KEYS.role, payload.roleId] });
        queryClient.invalidateQueries({
          queryKey: [GAMMA_ROLES_QUERY_KEYS.roleMembers, payload.roleId],
        });
      }

      add({ title: requestResultTitle(result.status), data: { status: 'success' } });
    },
    onError: (error) => {
      if (silent) return;

      add({
        title: getApiErrorMessage(error as TFetchError, 'Could not submit this role change.'),
        data: { status: 'error' },
      });
    },
  });
};

export const useApproveGammaRoleRequest = () => {
  const { invalidate, queryClient, showError, showSuccess } = useGammaRoleRequestMutation();

  return useMutation({
    mutationFn: (requestId: string) => gammaRoleRequestsClient.approve(requestId),
    onSuccess: () => {
      invalidate();
      showSuccess('Request approved.');
    },
    onError: async (error) => {
      if (isConflict(error)) {
        await queryClient.refetchQueries({
          queryKey: [GAMMA_ROLE_REQUEST_QUERY_KEYS.list],
          type: 'active',
        });
      }
      showError(error);
    },
  });
};

export const useRejectGammaRoleRequest = () => {
  const { invalidate, queryClient, showError, showSuccess } = useGammaRoleRequestMutation();

  return useMutation({
    mutationFn: ({ requestId, ...body }: RejectGammaRoleRequestBody & { requestId: string }) =>
      gammaRoleRequestsClient.reject(requestId, body),
    onSuccess: () => {
      invalidate();
      showSuccess('Request rejected.');
    },
    onError: async (error) => {
      if (isConflict(error)) {
        await queryClient.refetchQueries({
          queryKey: [GAMMA_ROLE_REQUEST_QUERY_KEYS.list],
          type: 'active',
        });
      }
      showError(error);
    },
  });
};
